import type { AlpacaCredentialSummary, CredentialCheck, CredentialSummary, PolygonCredentialSummary, TestCredentialKeysRequest } from '@fleece/models';
import { CircleCheck, CircleX, Eye, EyeOff, PlugZap, ShieldCheck } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { CopyButton } from '@/components/copy-button';
import { Modal } from '@/components/modal';
import { Button, ErrorNote, Field, Input, Segmented, Select, Spinner, Switch } from '@/components/ui';
import { useAccounts } from '@/hooks/use-ledger';
import { useCreateCredential, useCredentialSecret, useTestCredential, useTestCredentialKeys, useUpdateCredential } from '@/hooks/use-settings';
import {
  accountChoiceLabel,
  alpacaCreateRequest,
  alpacaDraftOf,
  alpacaDraftProblem,
  alpacaTestKeysRequest,
  alpacaUpdateRequest,
  checkDisplay,
  defaultAccountChoices,
  newAlpacaDraft,
  polygonCreateRequest,
  polygonDraftProblem,
  polygonTestKeysRequest,
  polygonUpdateRequest,
  withDefaultAccount,
  type AlpacaDraft,
  type CheckDisplay,
  type PolygonDraft,
} from '@/lib/settings';
import { cn } from '@/lib/utils';

/** A dialog's form: fields that scroll, and the actions pinned below them. */
export function DialogForm({ onSubmit, error, footer, children }: { onSubmit: () => void; error?: string; footer: ReactNode; children: ReactNode }) {
  return (
    <form
      className="flex min-h-0 flex-col"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex min-h-0 flex-col gap-3 overflow-y-auto px-3 py-3">
        {children}
        {error !== undefined && <ErrorNote>{error}</ErrorNote>}
      </div>
      <footer className="flex shrink-0 justify-end gap-2 border-t border-line px-3 py-2">{footer}</footer>
    </form>
  );
}

function SubmitButton({ busy, busyLabel, label, disabled }: { busy: boolean; busyLabel: string; label: string; disabled: boolean }) {
  return (
    <Button type="submit" variant="primary" disabled={disabled || busy}>
      {busy ? <Spinner className="border-accent-ink/40 border-t-accent-ink" /> : <ShieldCheck className="size-3.5" />}
      {busy ? busyLabel : label}
    </Button>
  );
}

/** A key field: masked until asked to show what was typed, never autocompleted, never spell-checked into a dictionary. */
function SecretInput({ value, onChange, placeholder, autoFocus }: { value: string; onChange: (value: string) => void; placeholder?: string; autoFocus?: boolean }) {
  const [shown, setShown] = useState(false);
  return (
    <span className="relative flex">
      <Input
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        autoFocus={autoFocus}
        className="pr-8 font-mono"
      />
      <button
        type="button"
        onClick={() => setShown(!shown)}
        aria-label={shown ? 'Hide what was typed' : 'Show what was typed'}
        aria-pressed={shown}
        className="absolute inset-y-0 right-0 flex w-8 items-center justify-center rounded-r-md text-muted hover:text-ink focus-visible:outline-2 focus-visible:outline-accent"
      >
        {shown ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
      </button>
    </span>
  );
}

/** A provider's answer, beside the form that asked. */
function CheckNote({ display }: { display: CheckDisplay }) {
  const Icon = display.tone === 'positive' ? CircleCheck : CircleX;
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-2 rounded-md border p-2.5 text-xs',
        display.tone === 'positive' ? 'border-positive/30 bg-positive/10 text-positive' : 'border-negative/30 bg-negative/10 text-negative',
      )}
    >
      <Icon className="mt-px size-3.5 shrink-0" />
      <div className="min-w-0 break-words">{display.message}</div>
    </div>
  );
}

interface FormTest {
  readonly pending: boolean;
  readonly check?: CredentialCheck;
  readonly error?: string;
  /** Asks about the typed keys when there are any, or else the saved credential's — which records the answer. */
  run(keys: TestCredentialKeysRequest | undefined, credentialId: string | undefined): void;
  reset(): void;
}

/** A form's Test, over the two routes that answer it. */
function useFormTest(): FormTest {
  const typed = useTestCredentialKeys();
  const saved = useTestCredential();
  const [last, setLast] = useState<'typed' | 'saved'>();
  const current = last === 'saved' ? saved : last === 'typed' ? typed : undefined;
  return {
    pending: typed.isPending || saved.isPending,
    check: current?.data?.check,
    error: current?.error?.message,
    run(keys, credentialId) {
      if (keys !== undefined) {
        setLast('typed');
        typed.mutate(keys);
      } else if (credentialId !== undefined) {
        setLast('saved');
        saved.mutate(credentialId);
      }
    },
    reset() {
      setLast(undefined);
      typed.reset();
      saved.reset();
    },
  };
}

function TestButton({ test, label, disabled, onClick }: { test: FormTest; label: string; disabled: boolean; onClick: () => void }) {
  return (
    <Button className="mr-auto" onClick={onClick} disabled={disabled || test.pending}>
      {test.pending ? <Spinner /> : <PlugZap className="size-3.5" />}
      {test.pending ? 'Testing…' : label}
    </Button>
  );
}

function TestResult({ test, accountId }: { test: FormTest; accountId?: string }) {
  if (test.error !== undefined) {
    return <ErrorNote>{test.error}</ErrorNote>;
  }
  return test.check === undefined ? null : <CheckNote display={checkDisplay(test.check, accountId)} />;
}

export interface AlpacaDialogProps {
  readonly open: boolean;
  /** Absent to add a new credential. */
  readonly editing?: AlpacaCredentialSummary;
  readonly onClose: () => void;
}

export function AlpacaDialog({ open, editing, onClose }: AlpacaDialogProps) {
  return (
    <Modal open={open} title={editing === undefined ? 'Add Alpaca account' : `Edit ${editing.name}`} onClose={onClose} className="w-[min(420px,calc(100vw-32px))]">
      <AlpacaForm editing={editing} onDone={onClose} />
    </Modal>
  );
}

function AlpacaForm({ editing, onDone }: { editing?: AlpacaCredentialSummary; onDone: () => void }) {
  const [typed, setDraft] = useState<AlpacaDraft>(() => (editing === undefined ? newAlpacaDraft() : alpacaDraftOf(editing)));
  const accounts = useAccounts();
  const choices = defaultAccountChoices(accounts.data?.accounts ?? [], typed.live);
  // A new credential's choice is settled on every render rather than in an effect, so
  // switching paper and live moves it to an account of the new type in the same frame the
  // switch is drawn — once the accounts are read. An edit is never settled: its type cannot
  // change, and a stale or unread list is no reason to swap its stored default for another.
  const draft = editing === undefined && accounts.data !== undefined ? withDefaultAccount(typed, choices) : typed;
  // Shown even when the list lacks it, so the select says what a save will send.
  const unlisted = draft.defaultAccountId !== '' && !choices.some((account) => account.accountId === draft.defaultAccountId);
  const create = useCreateCredential();
  const update = useUpdateCredential();
  const write = editing === undefined ? create : update;
  const problem = alpacaDraftProblem(draft, editing !== undefined);
  const test = useFormTest();
  const set = (change: Partial<AlpacaDraft>): void => {
    // An answer about other keys, or the other host, would read as one about these.
    if ('accessKey' in change || 'secretKey' in change || 'live' in change) {
      test.reset();
    }
    setDraft((current) => ({ ...current, ...change }));
  };

  const submit = (): void => {
    if (problem !== undefined) {
      return;
    }
    const done = { onSuccess: onDone };
    if (editing === undefined) {
      create.mutate(alpacaCreateRequest(draft), done);
    } else {
      update.mutate(alpacaUpdateRequest(editing, draft), done);
    }
  };

  const keysTyped = draft.accessKey.trim().length > 0 || draft.secretKey.trim().length > 0;
  const checking = editing === undefined || keysTyped;
  const typedKeys = alpacaTestKeysRequest(draft);

  return (
    <DialogForm
      onSubmit={submit}
      error={write.error?.message}
      footer={
        <>
          <TestButton
            test={test}
            label={editing !== undefined && !keysTyped ? 'Test saved keys' : 'Test'}
            // Both typed keys, or on an edit none, which tests the saved ones.
            disabled={typedKeys === undefined && (editing === undefined || keysTyped)}
            onClick={() => test.run(typedKeys, editing?.credentialId)}
          />
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <SubmitButton
            busy={write.isPending}
            busyLabel={checking ? 'Checking with Alpaca…' : 'Saving…'}
            label={checking ? 'Check and save' : 'Save'}
            disabled={problem !== undefined}
          />
        </>
      }
    >
      <Field label="Name">
        <Input value={draft.name} onChange={(event) => set({ name: event.target.value })} placeholder="Paper, Live IRA…" autoFocus={editing === undefined} />
      </Field>

      <div className="flex flex-col gap-1">
        <span className="text-[11px] text-ink-2">Account</span>
        {editing === undefined ? (
          <Segmented
            label="Account type"
            value={draft.live ? 'live' : 'paper'}
            onChange={(value) => set({ live: value === 'live' })}
            options={[
              { value: 'paper', label: 'Paper' },
              { value: 'live', label: 'Live' },
            ]}
          />
        ) : (
          <p className="text-xs text-ink">
            {editing.live ? 'Live' : 'Paper'} account <span className="font-mono">{editing.accountId}</span>
            <span className="text-muted"> — fixed once the keys were checked</span>
          </p>
        )}
        {draft.live && editing === undefined && <p className="text-[11px] text-warning">Real money. Fills in this account are booked from its order feed.</p>}
      </div>

      <Field label="Default account" hint="where orders nobody claims are booked">
        <Select
          value={draft.defaultAccountId}
          onChange={(event) => set({ defaultAccountId: event.target.value })}
          disabled={choices.length === 0}
          aria-describedby="default-account-note"
        >
          {unlisted && <option value={draft.defaultAccountId}>{draft.defaultAccountId}</option>}
          {choices.length === 0 && !unlisted && <option value="">{accounts.isPending ? 'Loading accounts…' : `No ${draft.live ? 'live' : 'paper'} account`}</option>}
          {choices.map((account) => (
            <option key={account.accountId} value={account.accountId}>
              {accountChoiceLabel(account)}
            </option>
          ))}
        </Select>
      </Field>
      <p id="default-account-note" className="-mt-1 text-[11px] text-muted">
        {accounts.error !== null
          ? `The accounts could not be read: ${accounts.error.message}`
          : choices.length === 0 && !accounts.isPending
            ? `Create a ${draft.live ? 'live' : 'paper'} account under Accounts first.`
            : `Orders placed by hand on Alpaca’s website, or claimed by no strategy in time. Only ${draft.live ? 'live' : 'paper'} accounts can hold them.`}
      </p>

      <Field label="API key ID" hint={editing === undefined ? undefined : 'leave both blank to keep the current keys'}>
        <SecretInput
          value={draft.accessKey}
          onChange={(accessKey) => set({ accessKey })}
          placeholder={editing === undefined ? 'PK… or AK…' : `current key ${editing.accessKeyHint}`}
        />
      </Field>
      <Field label="Secret key">
        <SecretInput value={draft.secretKey} onChange={(secretKey) => set({ secretKey })} />
      </Field>
      {checking && <p className="-mt-1 text-[11px] text-muted">Checked with Alpaca before saving. Alpaca says which account the keys belong to.</p>}
      <TestResult test={test} accountId={editing?.accountId} />

      <div className="flex flex-col gap-1.5 rounded-md border border-line p-2.5">
        <Switch checked={draft.trackOrders} onChange={(trackOrders) => set({ trackOrders })} label="Follow this account's orders" />
        <p className="pl-9 text-[11px] text-muted">Books every fill in this account to its virtual accounts.</p>
        <Switch checked={draft.supportData} onChange={(supportData) => set({ supportData })} label="Use for market data" />
        <p className="pl-9 text-[11px] text-muted">Quotes, bars and chains. Paper keys are used before live ones.</p>
      </div>
    </DialogForm>
  );
}

export interface PolygonDialogProps {
  readonly open: boolean;
  readonly editing?: PolygonCredentialSummary;
  readonly onClose: () => void;
}

export function PolygonDialog({ open, editing, onClose }: PolygonDialogProps) {
  return (
    <Modal open={open} title={editing === undefined ? 'Add Polygon key' : `Edit ${editing.name}`} onClose={onClose}>
      <PolygonForm editing={editing} onDone={onClose} />
    </Modal>
  );
}

function PolygonForm({ editing, onDone }: { editing?: PolygonCredentialSummary; onDone: () => void }) {
  const [draft, setDraft] = useState<PolygonDraft>(() => ({ name: editing?.name ?? '', apiKey: '' }));
  const create = useCreateCredential();
  const update = useUpdateCredential();
  const write = editing === undefined ? create : update;
  const problem = polygonDraftProblem(draft, editing !== undefined);
  const checking = draft.apiKey.trim().length > 0;
  const test = useFormTest();
  const typedKey = polygonTestKeysRequest(draft);

  const submit = (): void => {
    if (problem !== undefined) {
      return;
    }
    if (editing === undefined) {
      create.mutate(polygonCreateRequest(draft), { onSuccess: onDone });
    } else {
      update.mutate(polygonUpdateRequest(editing, draft), { onSuccess: onDone });
    }
  };

  return (
    <DialogForm
      onSubmit={submit}
      error={write.error?.message}
      footer={
        <>
          <TestButton
            test={test}
            label={editing !== undefined && !checking ? 'Test saved key' : 'Test'}
            disabled={typedKey === undefined && editing === undefined}
            onClick={() => test.run(typedKey, editing?.credentialId)}
          />
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <SubmitButton
            busy={write.isPending}
            busyLabel={checking ? 'Checking with Polygon…' : 'Saving…'}
            label={checking ? 'Check and save' : 'Save'}
            disabled={problem !== undefined}
          />
        </>
      }
    >
      <Field label="Name">
        <Input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder="Polygon" autoFocus={editing === undefined} />
      </Field>
      <Field label="API key" hint={editing === undefined ? undefined : 'leave blank to keep the current key'}>
        <SecretInput
          value={draft.apiKey}
          onChange={(apiKey) => {
            test.reset();
            setDraft({ ...draft, apiKey });
          }}
          placeholder={editing === undefined ? undefined : `current key ${editing.apiKeyHint}`}
        />
      </Field>
      <p className="-mt-1 text-[11px] text-muted">Used by the dividend job. Checked with Polygon before saving.</p>
      <TestResult test={test} />
    </DialogForm>
  );
}

export interface CredentialKeysDialogProps {
  /** The credential whose keys to show; absent while closed. */
  readonly credential?: CredentialSummary;
  readonly onClose: () => void;
}

/**
 * A saved credential's keys, in plain text, to read or copy. They are fetched when this
 * opens and dropped when it closes, rather than sent with the list where every row would
 * carry them.
 */
export function CredentialKeysDialog({ credential, onClose }: CredentialKeysDialogProps) {
  return (
    <Modal open={credential !== undefined} title={credential === undefined ? 'Keys' : `${credential.name} keys`} onClose={onClose} className="w-[min(480px,calc(100vw-32px))]">
      {credential !== undefined && <CredentialKeys credential={credential} onDone={onClose} />}
    </Modal>
  );
}

function CredentialKeys({ credential, onDone }: { credential: CredentialSummary; onDone: () => void }) {
  const secret = useCredentialSecret(credential.credentialId);
  const full = secret.data?.credential;

  return (
    <DialogForm
      onSubmit={onDone}
      error={secret.error?.message}
      footer={
        <Button type="submit" variant="primary">
          Done
        </Button>
      }
    >
      {credential.kind === 'alpaca' && (
        <p className="text-xs text-ink">
          {credential.live ? 'Live' : 'Paper'} account <span className="font-mono">{credential.accountId}</span>
        </p>
      )}
      {secret.isPending && (
        <span role="status" className="flex items-center gap-2 text-xs text-muted">
          <Spinner /> Fetching the keys…
        </span>
      )}
      {full?.kind === 'alpaca' && (
        <>
          <SecretValue label="API key ID" value={full.accessKey} />
          <SecretValue label="Secret key" value={full.secretKey} />
        </>
      )}
      {full?.kind === 'polygon' && <SecretValue label="API key" value={full.apiKey} />}
      {full !== undefined && (
        <p className={cn('text-[11px]', credential.kind === 'alpaca' && credential.live ? 'text-warning' : 'text-muted')}>
          {credential.kind === 'alpaca' && credential.live ? 'These keys trade real money. ' : ''}Anyone who sees them can use them.
        </p>
      )}
    </DialogForm>
  );
}

function SecretValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[11px] text-ink-2">{label}</span>
      <div className="flex items-start gap-1.5">
        <code className="min-w-0 flex-1 rounded-md border border-line bg-surface-2 px-2 py-1.5 font-mono text-xs break-all text-ink select-all">{value}</code>
        <CopyButton value={value} label={label} />
      </div>
    </div>
  );
}

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly message: ReactNode;
  readonly confirmLabel: string;
  readonly busy: boolean;
  readonly error?: string;
  /** Spelled out when the action moves real money or stops it being recorded. */
  readonly serious?: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

/** Says what an action costs, and does it only on a second, deliberate click. */
export function ConfirmDialog({ open, title, message, confirmLabel, busy, error, serious, onConfirm, onClose }: ConfirmDialogProps) {
  return (
    <Modal open={open} title={title} onClose={onClose}>
      <DialogForm
        onSubmit={onConfirm}
        error={error}
        footer={
          <>
            <Button variant="ghost" onClick={onClose} autoFocus>
              Cancel
            </Button>
            <Button type="submit" disabled={busy} className="border-negative/40 bg-negative text-white hover:opacity-90">
              {busy && <Spinner className="border-white/40 border-t-white" />}
              {confirmLabel}
            </Button>
          </>
        }
      >
        <div className={serious === true ? 'rounded-md border border-negative/30 bg-negative/10 p-2.5 text-xs text-ink' : 'text-xs text-ink-2'}>{message}</div>
      </DialogForm>
    </Modal>
  );
}
